import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Alert, Platform, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { observer } from '@legendapp/state/react';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import theme from '@/config/theme';
import { APP_CONSTANTS } from '@/config/constants';
import { supabase } from '@/services/supabase';
import { globalState } from '@/state/globalState';
import { hasDeveloperBypass } from '@/utils/subscriptionAccess';
import { useWebAlert } from '@/components/WebAlert';
import { DevChecklistExplorerLogger } from '@/utils/logger';

type ChecklistRow = {
  id: string;
  status: 'in_progress' | 'done';
  data_criacao?: string | number | null;
  data_conclusao?: string | number | null;
  sequencial_codigo?: string | null;
  sequencial_numero?: number | null;
  template?: { nome?: string | null } | null;
  usuario?: { name?: string | null; email?: string | null } | null;
};

type CompanyDetail = {
  id: string;
  nome?: string;
  cnpj?: string | null;
  plano?: string | null;
  email?: string | null;
  telefone?: string | null;
};

type DevExploreSubscription = {
  id: string;
  empresa_id: string;
  status: string;
  subscription_plan_id: string | null;
  start_date?: string | number | null;
  end_date?: string | number | null;
  next_billing_date?: string | number | null;
  external_subscription_id?: string | null;
};

type DevExplorePlan = {
  id: string;
  name?: string;
  price?: number | null;
};

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['ativa', 'cancelamento_agendado']);
const TRIAL_EXTERNAL_PREFIX = 'manual:trial-';
const PAGE_SIZE = 40;

const logger = DevChecklistExplorerLogger.Main;
const dataLogger = DevChecklistExplorerLogger.Data;

const normalizeTimestamp = (value?: string | number | null): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) {
      return asNumber < 1e12 ? asNumber * 1000 : asNumber;
    }
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const formatDateTime = (value?: string | number | null): string => {
  const normalized = normalizeTimestamp(value);
  if (!normalized) return '—';
  return new Date(normalized).toLocaleString('pt-BR');
};

const resolveSubscriptionLabel = (status?: string | null): string => {
  if (!status) return 'Inativa';
  const normalized = status.toLowerCase();
  if (normalized === 'ativa') return 'Ativa';
  if (normalized === 'cancelamento_agendado') return 'Cancelamento agendado';
  if (normalized === 'cancelada') return 'Cancelada';
  if (normalized === 'trial') return 'Trial';
  if (normalized === 'inativa') return 'Inativa';
  return status;
};

const resolveBillingProvider = (externalId?: string | null): string => {
  if (!externalId) return '—';
  if (externalId.startsWith('manual:apple:')) return 'App Store';
  return externalId.startsWith('manual:') ? 'PIX manual' : 'Stripe';
};

const StatusPill = ({ label, tone }: { label: string; tone: 'success' | 'warning' | 'error' | 'neutral' }) => {
  const toneColor = {
    success: theme.colors.util.success,
    warning: theme.colors.util.warning,
    error: theme.colors.util.error,
    neutral: theme.colors.text.tertiary,
  }[tone];
  return (
    <View style={[styles.pill, { borderColor: toneColor, backgroundColor: `${toneColor}22` }]}>
      <Text style={[styles.pillText, { color: toneColor }]}>{label}</Text>
    </View>
  );
};

const CompanyChecklistsScreen = observer(() => {
  const router = useRouter();
  const { id } = useLocalSearchParams();
  const { showAlert } = useWebAlert();
  const { width } = useWindowDimensions();
  const isDesktopLayout = width >= (APP_CONSTANTS.UI?.DESKTOP_BREAKPOINT ?? 1024);
  const userProfile = globalState.user.profile.get();
  const isDeveloper = hasDeveloperBypass(userProfile?.user_type);
  const isConnected = globalState.network.isConnected.get();

  const empresaId = Array.isArray(id) ? id[0] : id;

  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    statusLabel: string;
    planName: string;
    billingProvider: string;
    nextBillingDate?: string | number | null;
  } | null>(null);
  const [metrics, setMetrics] = useState<{ total: number; in_progress: number; done: number; last_activity: string | null } | null>(null);
  const [checklists, setChecklists] = useState<ChecklistRow[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'in_progress' | 'done'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isDeveloper) {
      const message = 'Acesso restrito: somente usuarios com perfil de desenvolvedor podem abrir este painel.';
      if (Platform.OS !== 'android') {
        showAlert('Acesso negado', message, [{ text: 'Voltar', onPress: () => router.back() }]);
      } else {
        Alert.alert('Acesso negado', message, [{ text: 'OK', onPress: () => router.back() }]);
      }
    }
  }, [isDeveloper, router, showAlert]);

  const loadCompany = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: companyData, error: companyError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME)
        .select('id, nome, cnpj, plano, email, telefone')
        .eq('id', empresaId)
        .single();

      if (companyError) throw companyError;

      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME)
        .select('id, empresa_id, status, subscription_plan_id, start_date, end_date, next_billing_date, external_subscription_id')
        .eq('empresa_id', empresaId);

      if (subscriptionsError) throw subscriptionsError;

      const { data: plansData, error: plansError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTION_PLANS.NAME)
        .select('id, name, price');

      if (plansError) throw plansError;

      setCompany(companyData as CompanyDetail);

      const plansById = new Map<string, DevExplorePlan>();
      (plansData ?? []).forEach(plan => {
        if (plan?.id) plansById.set(plan.id, plan);
      });

      const subscriptions = (subscriptionsData ?? []) as DevExploreSubscription[];
      const paidSubscriptions = subscriptions.filter(sub => sub?.external_subscription_id && !sub.external_subscription_id.startsWith(TRIAL_EXTERNAL_PREFIX));
      const trialSubscriptions = subscriptions.filter(sub => sub?.external_subscription_id && sub.external_subscription_id.startsWith(TRIAL_EXTERNAL_PREFIX));

      const resolveValidThrough = (sub: DevExploreSubscription): number | null => {
        const nextBilling = normalizeTimestamp(sub?.next_billing_date);
        const endDate = normalizeTimestamp(sub?.end_date);
        return Math.max(nextBilling ?? 0, endDate ?? 0) || null;
      };

      const resolveActivePaid = (): DevExploreSubscription | null => {
        const active = paidSubscriptions.filter(sub => {
          if (!ACTIVE_SUBSCRIPTION_STATUSES.has((sub?.status ?? '').toLowerCase())) return false;
          const validThrough = resolveValidThrough(sub);
          return !!validThrough && validThrough >= Date.now();
        });
        if (active.length === 0) return null;
        active.sort((a, b) => (resolveValidThrough(b) ?? 0) - (resolveValidThrough(a) ?? 0));
        return active[0];
      };

      const resolveActiveTrial = (): DevExploreSubscription | null => {
        const active = trialSubscriptions.filter(sub => {
          if (!ACTIVE_SUBSCRIPTION_STATUSES.has((sub?.status ?? '').toLowerCase())) return false;
          const trialEnd = normalizeTimestamp(sub?.end_date);
          return !!trialEnd && trialEnd >= Date.now();
        });
        if (active.length === 0) return null;
        active.sort((a, b) => (normalizeTimestamp(b?.end_date) ?? 0) - (normalizeTimestamp(a?.end_date) ?? 0));
        return active[0];
      };

      const activePaid = resolveActivePaid();
      const activeTrial = resolveActiveTrial();
      const lastPaid = paidSubscriptions.length > 0
        ? paidSubscriptions.sort((a, b) => (resolveValidThrough(b) ?? 0) - (resolveValidThrough(a) ?? 0))[0]
        : null;
      const lastTrial = trialSubscriptions.length > 0
        ? trialSubscriptions.sort((a, b) => (normalizeTimestamp(b?.end_date) ?? 0) - (normalizeTimestamp(a?.end_date) ?? 0))[0]
        : null;

      const primarySubscription = activePaid ?? activeTrial ?? lastPaid ?? lastTrial ?? null;
      const plan = primarySubscription?.subscription_plan_id ? plansById.get(primarySubscription.subscription_plan_id) : null;
      const status = activePaid
        ? 'ativa'
        : activeTrial
          ? 'trial'
          : 'inativa';

      setSubscriptionInfo({
        statusLabel: resolveSubscriptionLabel(status),
        planName: plan?.name ?? companyData?.plano ?? 'Sem plano',
        billingProvider: resolveBillingProvider(primarySubscription?.external_subscription_id ?? null),
        nextBillingDate: activePaid ? (activePaid?.next_billing_date ?? activePaid?.end_date ?? null) : null,
      });
    } catch (err) {
      logger.error('[empresa-checklists] Erro ao carregar empresa', err);
      setError('Erro ao carregar dados da empresa.');
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  const loadMetrics = useCallback(async () => {
    if (!empresaId) return;
    try {
      const { data, error: rpcError } = await supabase.rpc('dev_company_checklist_metrics', {
        empresa_ids: [empresaId],
      });
      if (rpcError) throw rpcError;
      const metric = (data ?? [])[0];
      if (metric) {
        setMetrics({
          total: metric.total,
          in_progress: metric.in_progress,
          done: metric.done,
          last_activity: metric.last_activity,
        });
      } else {
        setMetrics({ total: 0, in_progress: 0, done: 0, last_activity: null });
      }
    } catch (err) {
      dataLogger.warn('[empresa-checklists] Falha ao carregar métricas', err);
    }
  }, [empresaId]);

  const loadChecklists = useCallback(async (nextPage: number, replace = false) => {
    if (!empresaId) return;
    setLoadingList(true);
    try {
      const from = nextPage * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let query = supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME)
        .select(`
          id,
          status,
          data_criacao,
          data_conclusao,
          sequencial_codigo,
          sequencial_numero,
          template:guincho_templates_checklist(nome),
          usuario:guincho_usuarios(name, email)
        `)
        .eq('empresa_id', empresaId)
        .order('data_criacao', { ascending: false })
        .range(from, to);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (search.trim()) {
        const term = search.trim();
        query = query.or(`sequencial_codigo.ilike.%${term}%,id.ilike.%${term}%`);
      }

      const { data, error: listError } = await query;

      if (listError) throw listError;

      const rows = (data ?? []) as ChecklistRow[];
      setChecklists(prev => replace ? rows : [...prev, ...rows]);
      setHasMore(rows.length === PAGE_SIZE);
      setPage(nextPage);
    } catch (err) {
      logger.error('[empresa-checklists] Erro ao carregar checklists', err);
      setError('Erro ao carregar checklists.');
    } finally {
      setLoadingList(false);
    }
  }, [empresaId, search, statusFilter]);

  useEffect(() => {
    if (isDeveloper && isConnected) {
      loadCompany();
      loadMetrics();
      loadChecklists(0, true);
    }
  }, [isDeveloper, isConnected, loadCompany, loadMetrics]);

  useEffect(() => {
    if (!isDeveloper || !isConnected) return;
    const debounce = setTimeout(() => {
      loadChecklists(0, true);
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, statusFilter, isDeveloper, isConnected, loadChecklists]);

  const handleLoadMore = () => {
    if (loadingList || !hasMore) return;
    loadChecklists(page + 1);
  };

  const checklistRows = useMemo(() => {
    return checklists.map(item => {
      const templateName = item.template?.nome?.trim() || 'Template não identificado';
      const userName = item.usuario?.name?.trim() || item.usuario?.email?.trim() || 'Usuário não identificado';
      const sequential = item.sequencial_codigo?.trim()
        || (typeof item.sequencial_numero === 'number' ? `Checklist #${item.sequencial_numero}` : 'Sem numeração');
      return { ...item, templateName, userName, sequential };
    });
  }, [checklists]);

  if (!isDeveloper) {
    return null;
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
          <MaterialCommunityIcons name="chevron-left" size={22} color={theme.colors.text.primary} />
          <Text style={styles.backText}>Voltar</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => router.push('/painel-dev/explorar-checklists')} style={styles.secondaryButton}>
          <MaterialCommunityIcons name="domain" size={16} color={theme.colors.text.primary} />
          <Text style={styles.secondaryButtonText}>Empresas</Text>
        </TouchableOpacity>
      </View>

      {loading && !company ? (
        <View style={styles.loadingBox}>
          <ActivityIndicator color={theme.colors.primary.default} />
          <Text style={styles.loadingText}>Carregando dados da empresa...</Text>
        </View>
      ) : null}

      {company ? (
        <View style={styles.companyCard}>
          <View style={styles.companyTitleRow}>
            <View>
              <Text style={styles.companyTitle}>{company.nome || 'Empresa sem nome'}</Text>
              <Text style={styles.companySubtitle}>{company.cnpj || company.id}</Text>
            </View>
            {subscriptionInfo ? (
              <StatusPill
                label={subscriptionInfo.statusLabel}
                tone={subscriptionInfo.statusLabel === 'Ativa' ? 'success' : subscriptionInfo.statusLabel === 'Trial' ? 'warning' : 'neutral'}
              />
            ) : null}
          </View>
          {subscriptionInfo ? (
            <View style={styles.companyMetaRow}>
              <Text style={styles.companyMeta}>Plano: {subscriptionInfo.planName}</Text>
              <Text style={styles.companyMeta}>Provider: {subscriptionInfo.billingProvider}</Text>
              <Text style={styles.companyMeta}>Próx. vencimento: {formatDateTime(subscriptionInfo.nextBillingDate)}</Text>
            </View>
          ) : null}
          {metrics ? (
            <View style={styles.kpiRow}>
              <View style={styles.kpiCard}>
                <Text style={styles.kpiLabel}>Total</Text>
                <Text style={styles.kpiValue}>{metrics.total}</Text>
              </View>
              <View style={styles.kpiCard}>
                <Text style={styles.kpiLabel}>Em andamento</Text>
                <Text style={styles.kpiValue}>{metrics.in_progress}</Text>
              </View>
              <View style={styles.kpiCard}>
                <Text style={styles.kpiLabel}>Concluídos</Text>
                <Text style={styles.kpiValue}>{metrics.done}</Text>
              </View>
              <View style={styles.kpiCard}>
                <Text style={styles.kpiLabel}>Última atividade</Text>
                <Text style={styles.kpiValueSmall}>{formatDateTime(metrics.last_activity)}</Text>
              </View>
            </View>
          ) : null}
        </View>
      ) : null}

      {!isConnected ? (
        <View style={styles.alertCard}>
          <MaterialCommunityIcons name="wifi-off" size={18} color={theme.colors.util.error} />
          <Text style={styles.alertText}>Sem conexão. Esta tela funciona apenas online.</Text>
        </View>
      ) : null}

      {error ? (
        <View style={styles.alertCard}>
          <MaterialCommunityIcons name="alert-circle-outline" size={18} color={theme.colors.util.error} />
          <Text style={styles.alertText}>{error}</Text>
        </View>
      ) : null}

      <View style={styles.filters}>
        <View style={styles.searchRow}>
          <MaterialCommunityIcons name="magnify" size={18} color={theme.colors.text.tertiary} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Buscar por código ou ID"
            placeholderTextColor={theme.colors.text.tertiary}
            style={styles.searchInput}
          />
        </View>
        <View style={styles.filterRow}>
          {[
            { id: 'all', label: 'Todos' },
            { id: 'in_progress', label: 'Em andamento' },
            { id: 'done', label: 'Concluídos' },
          ].map(filter => {
            const active = statusFilter === filter.id;
            return (
              <TouchableOpacity
                key={filter.id}
                style={[styles.filterChip, active && styles.filterChipActive]}
                onPress={() => setStatusFilter(filter.id as any)}
              >
                <Text style={[styles.filterText, active && styles.filterTextActive]}>{filter.label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      {isDesktopLayout ? (
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, styles.cellCode]}>Checklist</Text>
            <Text style={[styles.tableCell, styles.cellTemplate]}>Template</Text>
            <Text style={[styles.tableCell, styles.cellUser]}>Usuário</Text>
            <Text style={[styles.tableCell, styles.cellStatus]}>Status</Text>
            <Text style={[styles.tableCell, styles.cellDate]}>Criado em</Text>
            <Text style={[styles.tableCell, styles.cellActions]}>Ações</Text>
          </View>
          {checklistRows.map(item => (
            <View key={item.id} style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.cellCode]} numberOfLines={1}>{item.sequential}</Text>
              <Text style={[styles.tableCell, styles.cellTemplate]} numberOfLines={1}>{item.templateName}</Text>
              <Text style={[styles.tableCell, styles.cellUser]} numberOfLines={1}>{item.userName}</Text>
              <View style={styles.cellStatus}>
                <StatusPill
                  label={item.status === 'done' ? 'Concluído' : 'Em andamento'}
                  tone={item.status === 'done' ? 'success' : 'warning'}
                />
              </View>
              <Text style={[styles.tableCell, styles.cellDate]}>{formatDateTime(item.data_criacao)}</Text>
              <View style={styles.cellActions}>
                <TouchableOpacity
                  style={styles.openButton}
                  onPress={() => router.push(`/painel-dev/checklist/${item.id}?returnTo=/painel-dev/empresa/${empresaId}`)}
                >
                  <Text style={styles.openButtonText}>Abrir</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>
      ) : (
        <View style={styles.cardList}>
          {checklistRows.map(item => (
            <View key={item.id} style={styles.checklistCard}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>{item.sequential}</Text>
                <StatusPill
                  label={item.status === 'done' ? 'Concluído' : 'Em andamento'}
                  tone={item.status === 'done' ? 'success' : 'warning'}
                />
              </View>
              <Text style={styles.cardSubtitle}>{item.templateName}</Text>
              <Text style={styles.cardSubtitle}>{item.userName}</Text>
              <Text style={styles.cardSubtitle}>Criado em {formatDateTime(item.data_criacao)}</Text>
              <TouchableOpacity
                style={styles.openButton}
                onPress={() => router.push(`/painel-dev/checklist/${item.id}?returnTo=/painel-dev/empresa/${empresaId}`)}
              >
                <Text style={styles.openButtonText}>Abrir</Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      {loadingList ? (
        <View style={styles.loadingList}>
          <ActivityIndicator color={theme.colors.primary.default} />
          <Text style={styles.loadingText}>Carregando checklists...</Text>
        </View>
      ) : null}

      {!loadingList && hasMore ? (
        <TouchableOpacity style={styles.loadMoreButton} onPress={handleLoadMore}>
          <Text style={styles.loadMoreText}>Carregar mais</Text>
        </TouchableOpacity>
      ) : null}
    </ScrollView>
  );
});

export default CompanyChecklistsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  content: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  backText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.primary,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  secondaryButtonText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.primary,
  },
  companyCard: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    gap: theme.spacing.sm,
  },
  companyTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  companyTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.text.primary,
  },
  companySubtitle: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  companyMetaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  companyMeta: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  kpiRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  kpiCard: {
    flex: 1,
    minWidth: 120,
    backgroundColor: theme.colors.background.secondary,
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
  },
  kpiLabel: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  kpiValue: {
    fontSize: theme.fontSizes.md,
    fontWeight: '600' as any,
    color: theme.colors.text.primary,
  },
  kpiValueSmall: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.primary,
  },
  loadingBox: {
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  loadingText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  alertCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.util.error + '22',
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
  },
  alertText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    flex: 1,
  },
  filters: {
    gap: theme.spacing.sm,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
  },
  searchInput: {
    flex: 1,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  filterChip: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  filterChipActive: {
    backgroundColor: theme.colors.primary.default,
    borderColor: theme.colors.primary.default,
  },
  filterText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  filterTextActive: {
    color: theme.colors.text.inverse,
  },
  table: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.colors.background.tertiary,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
    alignItems: 'center',
  },
  tableCell: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  cellCode: {
    flex: 1.1,
  },
  cellTemplate: {
    flex: 1.3,
  },
  cellUser: {
    flex: 1.2,
  },
  cellStatus: {
    flex: 0.8,
  },
  cellDate: {
    flex: 1,
  },
  cellActions: {
    flex: 0.7,
    alignItems: 'flex-end',
  },
  cardList: {
    gap: theme.spacing.md,
  },
  checklistCard: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    gap: theme.spacing.xs,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: '600' as any,
  },
  cardSubtitle: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  openButton: {
    marginTop: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.primary.default,
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  openButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600' as any,
  },
  loadMoreButton: {
    alignSelf: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  loadMoreText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
  loadingList: {
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  pill: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 4,
  },
  pillText: {
    fontSize: theme.fontSizes.xs,
    fontWeight: '600' as any,
  },
});
